import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { useColorScheme } from "../../hooks/useColorScheme";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { apiFetch } from "../../../api";
import { requestLock } from "../../../lockBus";
import { setTheme } from "../../../themeState";
import { AvatarImage } from "../../components/AvatarImage";
import { getPushSubscriptionStatus, subscribeToPush, unsubscribeFromPush } from "../../../webPush";
import { showToast } from "../../../toast";
import { EditNameSheet } from "./EditNameSheet";
import { ChangePhotoActionSheet } from "./ChangePhotoActionSheet";

// Full-screen dedicated page replacing the old ProfileDialog modal - portals
// into #profileView (route-driven visibility, see ProfileRouteScreen.tsx),
// same container-portal shape as ChatThreadScreen.tsx. Identity (name/email/
// avatar) is read from the shared appState fields seeded in AuthGate.tsx,
// not fetched independently here - this screen is one of the writers that
// keeps that shared state current after a name/avatar change, alongside
// CoupleHeader.tsx as a reader.
export function ProfileScreen() {
  const navigate = useNavigate();
  const mode = useColorScheme();
  const email = useAppState((s) => s.currentUserEmail);
  const fullName = useAppState((s) => s.currentUserFullName);
  const avatarFilename = useAppState((s) => s.currentUserAvatarFilename);

  const [editNameOpen, setEditNameOpen] = useState(false);
  const [changePhotoOpen, setChangePhotoOpen] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [pushStatus, setPushStatus] = useState<{ supported: boolean; configured: boolean; subscribed: boolean }>({
    supported: false,
    configured: false,
    subscribed: false,
  });
  const [pushBusy, setPushBusy] = useState(false);
  // Defaults true (redacted) until /api/me says otherwise - matches the
  // server's own default-true reasoning (pushPayloadForNotification), so
  // this never briefly shows the less-private state as current.
  const [previewsRedacted, setPreviewsRedacted] = useState(true);
  const [redactionBusy, setRedactionBusy] = useState(false);
  // Just for the "Active sessions  2 ›" count on this row - SessionsScreen.tsx
  // fetches the full list itself when actually navigated to; duplicating a
  // single lightweight GET here (rather than lifting the list into shared
  // state for one label) keeps that page's own fetch/revoke logic untouched.
  const [sessionCount, setSessionCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/api/me");
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setPreviewsRedacted(data.notificationPreviewsRedacted !== false);
      } catch {
        // Best effort - the toggle just starts at its safe default.
      }
    })();
    getPushSubscriptionStatus().then((s) => {
      if (!cancelled) setPushStatus(s);
    });
    apiFetch("/api/sessions")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && Array.isArray(data?.sessions)) setSessionCount(data.sessions.length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleTogglePush(): Promise<void> {
    setPushBusy(true);
    try {
      const ok = pushStatus.subscribed ? await unsubscribeFromPush() : await subscribeToPush();
      if (!ok) {
        showToast(
          pushStatus.subscribed
            ? "Failed to disable notifications."
            : "Failed to enable notifications - check your browser's notification permission for this site.",
          "error",
        );
        return;
      }
      setPushStatus(await getPushSubscriptionStatus());
    } finally {
      setPushBusy(false);
    }
  }

  async function handleToggleRedaction(): Promise<void> {
    const next = !previewsRedacted;
    setRedactionBusy(true);
    try {
      const res = await apiFetch("/api/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notificationPreviewsRedacted: next }),
      });
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) {
        showToast("Failed to update notification privacy.", "error");
        return;
      }
      setPreviewsRedacted(next);
    } catch {
      showToast("Server error. Try again.", "error");
    } finally {
      setRedactionBusy(false);
    }
  }

  async function handlePhotoPicked(file: File): Promise<void> {
    setChangePhotoOpen(false);
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const res = await apiFetch("/api/profile/avatar", { method: "POST", body: fd });
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showToast(data.message || "Failed to update profile picture.", "error");
        return;
      }
      const data = await res.json();
      appState.currentUserAvatarFilename = data.avatarFilename || null;
      notifyStateChange();
      showToast("Profile picture updated.", "success");
    } catch {
      showToast("Server error. Try again.", "error");
    } finally {
      setUploadingAvatar(false);
    }
  }

  function handleThemeToggle(): void {
    setTheme(mode === "dark" ? "light" : "dark");
  }

  function handleLogOut(): void {
    // Matches the tab bar's own Lock button (AppNav.tsx) exactly - "lock"
    // and "log out this device" are the same underlying action in this app
    // (see AuthGate.tsx's lock handler, which calls /api/logout), and that
    // existing entry point has no confirmation step today, so this doesn't
    // invent one either.
    requestLock("manual");
  }

  const container = getVanillaEl<HTMLDivElement>("profileView");
  if (!container) return null;

  return createPortal(
    <>
      <div className="profile-page-header">
        <button type="button" className="btn btn-outline-pink btn-sm" aria-label="Back" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left" aria-hidden="true" />
        </button>
        <span className="profile-page-header-title">Profile</span>
      </div>

      <div className="profile-page-content">
        <div className="profile-hero">
          <div className="profile-hero-avatar-wrap">
            <div className="profile-hero-avatar">
              {avatarFilename ? (
                <AvatarImage
                  filename={avatarFilename}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  fallback={<i className="bi bi-person-fill" aria-hidden="true" />}
                />
              ) : (
                <i className="bi bi-person-fill" aria-hidden="true" />
              )}
            </div>
            <button
              type="button"
              className="profile-hero-avatar-btn"
              aria-label="Change profile photo"
              disabled={uploadingAvatar}
              onClick={() => setChangePhotoOpen(true)}
            >
              <i className={`bi ${uploadingAvatar ? "bi-arrow-repeat" : "bi-camera-fill"}`} aria-hidden="true" />
            </button>
          </div>
          <div className="profile-hero-name">{fullName || "Add your name"}</div>
          <div className="profile-hero-email">{email}</div>
        </div>

        <div className="profile-section-label">Personal information</div>
        <div className="profile-group">
          <button type="button" className="profile-row" onClick={() => setEditNameOpen(true)}>
            <span className="profile-row-icon"><i className="bi bi-person" aria-hidden="true" /></span>
            <span className="profile-row-body">
              <span className="profile-row-label">Name</span>
              <span className="profile-row-value">{fullName || "Not set"}</span>
            </span>
            <span className="profile-row-chevron"><i className="bi bi-chevron-right" aria-hidden="true" /></span>
          </button>
          <div className="profile-row profile-row-static" title="Email can't be changed here">
            <span className="profile-row-icon"><i className="bi bi-envelope" aria-hidden="true" /></span>
            <span className="profile-row-body">
              <span className="profile-row-label">Email</span>
              <span className="profile-row-value">{email}</span>
            </span>
            <span className="profile-row-chevron"><i className="bi bi-lock-fill" aria-hidden="true" style={{ fontSize: "0.75rem" }} /></span>
          </div>
        </div>

        {pushStatus.supported && pushStatus.configured && (
          <>
            <div className="profile-section-label">Notifications</div>
            <div className="profile-group">
              <div className="profile-row profile-row-static">
                <span className="profile-row-icon"><i className="bi bi-bell" aria-hidden="true" /></span>
                <span className="profile-row-body">
                  <span className="profile-row-label">Notifications on this device</span>
                  <span className="profile-row-value">
                    {pushStatus.subscribed ? "You'll be notified here even when the app isn't open." : "Off"}
                  </span>
                </span>
                <span className="profile-row-action">
                  <button
                    type="button"
                    className={`btn btn-sm ${pushStatus.subscribed ? "btn-outline-pink" : "btn-pink"}`}
                    onClick={handleTogglePush}
                    disabled={pushBusy}
                  >
                    {pushBusy ? "…" : pushStatus.subscribed ? "Turn off" : "Turn on"}
                  </button>
                </span>
              </div>
              <div className="profile-row profile-row-static">
                <span className="profile-row-icon"><i className="bi bi-eye-slash" aria-hidden="true" /></span>
                <span className="profile-row-body">
                  <span className="profile-row-label">Show message content in previews</span>
                  <span className="profile-row-value">
                    {previewsRedacted ? "Lock-screen notifications hide message text." : "Lock-screen notifications show message text."}
                  </span>
                </span>
                <span className="profile-row-action">
                  <button
                    type="button"
                    className={`btn btn-sm ${previewsRedacted ? "btn-pink" : "btn-outline-pink"}`}
                    onClick={handleToggleRedaction}
                    disabled={redactionBusy}
                  >
                    {redactionBusy ? "…" : previewsRedacted ? "Turn on" : "Turn off"}
                  </button>
                </span>
              </div>
            </div>
          </>
        )}

        <div className="profile-section-label">Privacy &amp; security</div>
        <div className="profile-group">
          <button type="button" className="profile-row" onClick={() => navigate("/profile/sessions")}>
            <span className="profile-row-icon"><i className="bi bi-shield-lock" aria-hidden="true" /></span>
            <span className="profile-row-body">
              <span className="profile-row-label">Active sessions</span>
            </span>
            {sessionCount !== null && <span className="profile-row-value">{sessionCount}</span>}
            <span className="profile-row-chevron"><i className="bi bi-chevron-right" aria-hidden="true" /></span>
          </button>
        </div>

        <div className="profile-section-label">Appearance</div>
        <div className="profile-group">
          <button type="button" className="profile-row" onClick={handleThemeToggle}>
            <span className="profile-row-icon">
              <i className={`bi ${mode === "dark" ? "bi-moon-stars-fill" : "bi-sun-fill"}`} aria-hidden="true" />
            </span>
            <span className="profile-row-body">
              <span className="profile-row-label">Theme</span>
            </span>
            <span className="profile-row-value">{mode === "dark" ? "Dark" : "Light"}</span>
          </button>
        </div>

        <div className="profile-section-label">Account</div>
        <div className="profile-group">
          <button type="button" className="profile-row profile-danger" onClick={handleLogOut}>
            <span className="profile-row-icon"><i className="bi bi-box-arrow-right" aria-hidden="true" /></span>
            <span className="profile-row-body">
              <span className="profile-row-label">Log out</span>
            </span>
          </button>
        </div>

        <div className="profile-footer-note">Private &middot; Encrypted</div>
      </div>

      {editNameOpen && <EditNameSheet currentName={fullName} onClose={() => setEditNameOpen(false)} />}
      {changePhotoOpen && <ChangePhotoActionSheet onClose={() => setChangePhotoOpen(false)} onPicked={handlePhotoPicked} />}
    </>,
    container,
  );
}
